import { createSelectSchema, FieldSchema, FieldType, Schema } from "../../hooks/useSchema";

export interface CRMConfig {
    apiPath: string;
    pagePath: string;
    checkCanExport: (user: any) => boolean;
}

export interface CRMAttributeConfig extends FieldSchema {
  code: string;
  available_for_categories?: string[];
  select_options?: { value: string, label: string }[];
}

export const CustomAttributeType = {
  suggest: "suggest",
  suggest_multi: "suggest_multi",
}

export interface CRMCategoryConfig {
  label: string;
  code: string;
}

export interface CRMSettings {
  contact_attributes?: CRMAttributeConfig[];
  contact_categories?: CRMCategoryConfig[];

  custom_attributes_suggestions?: Record<string, string[]>;
}

export interface DescribedAddress {
  value: string;
  description?: string;
}

const ConsentOptions = [
  { value: "yes", label: "да" },
  { value: "no", label: "нет" },
  { value: "recalled", label: "отозвано" },
]

export interface Contact {
  _id: string; // Assuming UUIDs are represented as strings in JSON

  first_name: string; // :not-null true
  last_name?: string;
  middle_name?: string;

  gender?: string;
  birth_date?: string; // Date strings are usually passed as ISO 8601 strings in JSON

  emails?: DescribedAddress[]; // Assuming array of email strings
  phones?: DescribedAddress[]; // Assuming array of phone strings

  // proxy fields
  email?: string;
  phone?: string;

  description?: string;

  owner_id?: string; // UUID represented as string
  do_not_contact?: boolean;

  tags?: number[]; // :bigint-array translates to array of numbers
  categories?: string[];

  geo_country?: string[];
  geo_city?: string[];
  communication_language?: string[];

  consent_pd?: string;
  consent_emails?: string;

  attributes?: Record<string, any>; // :jsonb usually translates to an object/map

  organization_name?: string;
  organization_position?: string;
  organization_is_active?: boolean;
  organizations_links?: any[]; // Adjust type if you have a specific interface for links
  
  // virtual - for creation
  organization_link: any;

  merge_target_id?: string;
  merge_source_id?: string;

  created_at: string; // ISO 8601 timestamp string
  created_by: string; // Assuming UUID/ID string
  updated_at: string; // ISO 8601 timestamp string
  updated_by: string; // Assuming UUID/ID string
}



export const ContactSchema: Schema = {
  _id: { label: "ID контакта" }, // Standard ID behavior based on your snippet

  name: { label: "ФИО" },
  last_name: { label: "Фамилия" },
  first_name: { label: "Имя" }, // Note: required field based on clj schema
  middle_name: { label: "Отчество" },

  gender: createSelectSchema(
    [{ value: "f", label: "Ж"}, { value: "m", label: "М"}],
    { label: "Пол", }
  ),
  birth_date: { type: FieldType.date, label: "Дата рождения" },

  emails: { label: "Email'ы" },
  phones: { label: "Телефоны" },
  
  // proxy fields
  phone: { label: "Телефон" },
  email: { label: "Email" },


  description: { type: FieldType.textlong, label: "Описание" },

  owner_id: { type: FieldType.dictionarySelect, dictionary: "Users", label: "Ответственный" },
  do_not_contact: { type: FieldType.bool, label: "Не контактировать" },

  categories: { type: FieldType.multiselect, label: "Категории" },
  tags: { type: FieldType.multiselect, label: "Теги" },
  geo_country: { type: FieldType.multiselect, label: "Страны" },
  geo_city: { type: FieldType.multiselect, label: "Города" },
  
  consent_pd: createSelectSchema(ConsentOptions, { label: "Согласие ПД"}),
  consent_emails: createSelectSchema(ConsentOptions, { label: "Согласие на рассылки"}),
  communication_language: createSelectSchema([
    { value: "ru", label: "ru" },
    { value: "en", label: "en" },
  ], { label: "Язык коммуникации", type: FieldType.multiselect}),
  
  attributes: { label: "Кастомные атрибуты" },

  organization_name: { label: "Компания" },
  organization_position: { label: "Должность" },
  organizations_links: { label: "Связи с компаниями" },

  merge_target_id: { label: "ID цели слияния" },
  merge_source_id: { label: "ID источника слияния" },

  created_at: { type: FieldType.datetime, label: "Создан" },
  created_by: { type: FieldType.dictionarySelect, dictionary: "Users", label: "Кем создан" },
  updated_at: { type: FieldType.datetime, label: "Обновлен" },
  updated_by: { type: FieldType.dictionarySelect, dictionary: "Users", label: "Кем обновлен" }
}


export interface Organization {
  _id: string;
  name: string; // Required based on :not-null true
  description?: string;
  owner_id?: string;

  created_at: string;
  created_by: string;
  updated_at: string;
  updated_by: string;
}


export const OrganizationSchema: Schema = {
  _id: { label: "ID" },
  
  name: { type: FieldType.text, label: "Название" },
  description: { type: FieldType.textlong, label: "Комментарии" },
  
  owner_id: { type: FieldType.dictionarySelect, dictionary: "Users", label: "Ответственный" },

  created_at: { type: FieldType.datetime, label: "Создана" },
  created_by: { type: FieldType.dictionarySelect, dictionary: "Users", label: "Кем создана" },
  updated_at: { type: FieldType.datetime, label: "Обновлена" },
  updated_by: { type: FieldType.dictionarySelect, dictionary: "Users", label: "Кем обновлена" }
};

export interface ContactOrgLink {
  _id?: number;
  contact_id: string;
  organization_id: string;
  organization_name?: string;
  title?: string;
  is_active?: boolean;
}

export interface ContactExtraInfo {
  organizations: ContactOrgLink[];
  notes: ContactNote[];
  events: any[];
}




export interface Tag {
  _id: number;
  name: string;
  description?: string;
  color?: string;
  created_at: string;
  created_by: string;
  updated_at: string;
  updated_by: string;
}

export const TAG_COLORS = [
  // Cool
  { value: "blue",      color: "#919CE9", label: "Синий" },        // existing, muted periwinkle
  { value: "indigo",    color: "#C5CAE9", label: "Индиго" },       // Indigo 100
  { value: "lightblue", color: "#b3d6fc", label: "Светло-синий" }, // Light Blue 100
  { value: "cyan",      color: "#B2EBF2", label: "Голубой" },      // Cyan 100
  { value: "teal",      color: "#c0fef9", label: "Бирюзовый" },    // Teal 100
  { value: "greyblue",  color: "#96a7c3", label: "Серо-синий" },   // Blue Grey 100

  // Green / yellow
  { value: "green",      color: "#C8E6C9", label: "Зеленый" },       // Green 100
  { value: "lightgreen", color: "#DCEDC8", label: "Салатовый" },     // Light Green 100
  { value: "lime",       color: "#f3fc8d", label: "Лаймовый" },      // Lime 100
  { value: "yellow",     color: "#feef67", label: "Желтый" },        // Yellow 100
  { value: "amber",      color: "#f9de8e", label: "Янтарный" },      // Amber 100

  // Warm
  { value: "orange",     color: "#FFCCBC", label: "Оранжевый" },     // existing; actually Deep Orange 100
  { value: "yelloworange", color: "#fcc28c", label: "Светло-оранжевый" }, // Orange 100
  { value: "red",        color: "#f8939d", label: "Красный" },       // Red 100
  { value: "brown",      color: "#cb9a88", label: "Коричневый" },    // Brown 100

  // Purple / pink
  { value: "purple",     color: "#e2a8ec", label: "Фиолетовый" },    // Purple 100
  { value: "deeppurple", color: "#c4b0eb", label: "Темно-фиолетовый" }, // Deep Purple 100
  { value: "pink",       color: "#faa2c1", label: "Розовый" },       // Pink 100
];

export const TAG_COLORS_VALUES = TAG_COLORS.map(t => t.value);

export const getRandomTagColor = () => TAG_COLORS_VALUES[Math.floor(Math.random() * TAG_COLORS_VALUES.length)];

export const TagSchema: Schema = {
  _id: { label: "ID" },
  name: { type: FieldType.text, label: "Название" },
  description: { type: FieldType.text, label: "Комментарий" },
  color: createSelectSchema(TAG_COLORS, { label: "Цвет" }),
  
  created_at: { type: FieldType.datetime, label: "Создан" },
  created_by: { type: FieldType.dictionarySelect, dictionary: "Users", label: "Кем создан" },
  updated_at: { type: FieldType.datetime, label: "Обновлен" },
  updated_by: { type: FieldType.dictionarySelect, dictionary: "Users", label: "Кем обновлен" }
};




export interface ContactNote {
  _id: string;
  contact_id: string;
  message: string;
  remind_date?: string;
  created_at: string;
  created_by: string;
  updated_at: string;
  updated_by: string;
}

export const ContactNoteSchema: Schema = {
  message: { type: FieldType.textlong, label: "Текст", label_id: "crm.contact.note.message" },
  created_at: { type: FieldType.datetime, label: "Создана", label_id: "crm.contact.note.created_at" },
  created_by: { type: FieldType.dictionarySelect, dictionary: "Users", label_id: "crm.contact.note.created_by" },
};